import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useFeatureFlags } from '@/lib/feature-flags';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { Pagination } from '@/components/tools/pagination';
import {
  apiClient,
  type Agent,
  type AgentDetail,
  type AgentConnection,
  type AgentToolsResponse,
  type AuditLogEntry,
  type CredentialStatus,
  type CedarPolicy,
  type ProxySecretStatus,
  type PublicConfig,
  type DelegationAppCandidate,
} from '@/lib/api-client';
import {
  ArrowLeft,
  Cloud,
  Copy,
  Edit,
  Eye,
  EyeOff,
  GitMerge,
  Globe,
  Check,
  ChevronDown,
  ChevronRight,
  Key,
  Link2,
  Link2Off,
  Loader2,
  Plus,
  RefreshCw,
  Server,
  Shield,
  ShieldOff,
  Trash2,
  Wrench,
  X,
} from 'lucide-react';
import {
  validateRedirectPattern,
  validateRedirectPatterns,
  RedirectPatternError,
} from '@/lib/validation/redirect-pattern';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MergeResourcesDialog } from '@/components/resources/MergeResourcesDialog';

// ---------------------------------------------------------------------------
// Tab definitions
// ---------------------------------------------------------------------------
type TabId = 'connections' | 'configuration' | 'tools' | 'policies' | 'audit';
type TabDef = { id: TabId; label: string };

const SUPPORTED_CONNECTION_TYPES = [
  'IDENTITY_ASSERTION_CUSTOM_AS',
  'STS_VAULT_SECRET', 'SECRET',
  'STS_SERVICE_ACCOUNT', 'SERVICE_ACCOUNT',
  'STS_ACCESS_TOKEN',
];

// ---------------------------------------------------------------------------
// Main agent detail page
// ---------------------------------------------------------------------------
export default function AgentDetailPage() {
  const params = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const agentId = params.agentId as string;
  const { toast } = useToast();

  const [activeTab, setActiveTab] = useState<TabId>('connections');
  const [loading, setLoading] = useState(true);
  const [agent, setAgent] = useState<Agent | null>(null);
  const [detail, setDetail] = useState<AgentDetail | null>(null);
  const [syncing, setSyncing] = useState(false);
  const { publicConfig, isEnabled } = useFeatureFlags();
  const cedarEnabled = isEnabled('cedarEnabled');

  const tabs: TabDef[] = useMemo(() => {
    const base: TabDef[] = [
      { id: 'connections', label: 'Connections & Resources' },
      { id: 'configuration', label: 'Configuration' },
      { id: 'tools', label: 'Tools' },
    ];
    if (cedarEnabled) base.push({ id: 'policies', label: 'Policies' });
    base.push({ id: 'audit', label: 'Audit Log' });
    return base;
  }, [cedarEnabled]);

  useEffect(() => {
    if (!cedarEnabled && activeTab === 'policies') setActiveTab('connections');
  }, [cedarEnabled, activeTab]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [agentData, detailData] = await Promise.all([
        apiClient.getAgent(agentId),
        apiClient.getAgentDetail(agentId).catch(() => null),
      ]);
      setAgent(agentData);
      setDetail(detailData);
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [agentId, toast]);

  useEffect(() => {
    if (agentId) loadData();
  }, [agentId, loadData]);

  const handleSync = async () => {
    if (!agent?.okta_ai_agent_id) return;
    setSyncing(true);
    try {
      const result = await apiClient.syncOktaAgent(agent.okta_ai_agent_id);
      toast({
        title: result.success ? 'Synced' : 'Sync Failed',
        description: result.message,
        variant: result.success ? 'default' : 'destructive',
      });
      await loadData();
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSyncing(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm(`Delete agent "${agentId}"? This cannot be undone.`)) return;
    try {
      await apiClient.deleteAgent(agentId);
      toast({ title: 'Deleted', description: `Agent ${agentId} deleted` });
      navigate('/dashboard/agents');
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard/agents')}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Agents
        </Button>
        <p className="text-muted-foreground">Agent not found.</p>
      </div>
    );
  }

  const isOktaImported = agent.registration_source === 'okta-import';
  const stats = detail?.stats;

  // Decrement unlinked_count for not-supported connection types since they
  // can never be configured and should not show as actionable.
  if (stats) {
    stats.unlinked_count = Math.max(0, stats.unlinked_count - (detail?.connections ?? []).filter(
      (c) => c.status === 'ACTIVE' && !c.resource && !SUPPORTED_CONNECTION_TYPES.includes(c.connection_type)
    ).length);
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Link to="/dashboard/agents" className="hover:underline">Agents</Link>
        <span>/</span>
        <span className="text-foreground font-medium">{agent.agent_id}</span>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            {agent.enabled ? (
              <Shield className="w-6 h-6 text-green-600" />
            ) : (
              <ShieldOff className="w-6 h-6 text-gray-400" />
            )}
            <h1 className="text-3xl font-bold tracking-tight">{agent.agent_id}</h1>
            {isOktaImported && <span title="Imported from Okta"><Cloud className="w-5 h-5 text-blue-500" /></span>}
          </div>
          <p className="text-muted-foreground mt-1">
            {isOktaImported ? (
              <>
                Okta AI Agent &middot; Imported
                {stats ? ` · ${stats.total_connections} connection${stats.total_connections !== 1 ? 's' : ''}` : ''}
                {agent.okta_ai_agent_id && (
                  <> &middot; <code className="text-xs">{agent.okta_ai_agent_id}</code></>
                )}
              </>
            ) : (
              <>Manual agent &middot; <code className="text-xs">{agent.client_id}</code></>
            )}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {isOktaImported && agent.okta_ai_agent_id && (
            <Button variant="outline" size="sm" onClick={handleSync} disabled={syncing}>
              {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Sync from Okta
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setActiveTab('configuration')}>
            Configure
          </Button>
          <Button variant="destructive" size="sm" onClick={handleDelete}>
            <Trash2 className="w-4 h-4 mr-2" /> Delete
          </Button>
        </div>
      </div>

      {/* Stats row */}
      {stats && (
        <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
          <StatCard label="Total Connections" value={stats.total_connections} />
          <StatCard label="Linked" value={stats.fully_linked_count} color="green" />
          <StatCard label="Needs Resource" value={stats.unlinked_count} color={stats.unlinked_count > 0 ? 'amber' : undefined} />
          <StatCard label="Total Resources" value={stats.total_resources} />
        </div>
      )}

      {/* Tabs */}
      <div className="border-b">
        <nav className="flex gap-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-3 text-sm font-medium transition-colors border-b-2 ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'connections' && (
        <ConnectionsTab detail={detail} agentId={agentId} onRefresh={loadData} />
      )}
      {activeTab === 'configuration' && (
        <ConfigurationTab agent={agent} onSaved={loadData} publicConfig={publicConfig} />
      )}
      {activeTab === 'tools' && (
        <ToolsTab detail={detail} agentId={agentId} />
      )}
      {cedarEnabled && activeTab === 'policies' && (
        <PoliciesTab agentId={agentId} />
      )}
      {activeTab === 'audit' && (
        <AuditTab agentId={agentId} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stat card
// ---------------------------------------------------------------------------
function StatCard({ label, value, color }: { label: string; value: number; color?: string }) {
  const colorCls =
    color === 'green' ? 'text-green-600' :
    color === 'amber' ? 'text-amber-600' :
    '';
  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className={`text-2xl font-bold ${colorCls}`}>{value}</p>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function slugify(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'resource';
}

const SLUG_PATTERN = /^[a-z]([a-z0-9-]*[a-z0-9])?$/;


// ---------------------------------------------------------------------------
// Connection type badge helpers
// ---------------------------------------------------------------------------
type ConnectionFilter = 'all' | 'active' | 'unlinked' | 'inactive' | 'XAA' | 'SECRET' | 'SERVICE_ACCOUNT';

function connTypeBadge(type: string) {
  switch (type) {
    case 'IDENTITY_ASSERTION_CUSTOM_AS':
      return { label: 'XAA', cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' };
    case 'STS_VAULT_SECRET':
    case 'SECRET':
      return { label: 'Secret / PSK', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200' };
    case 'STS_SERVICE_ACCOUNT':
    case 'SERVICE_ACCOUNT':
      return { label: 'Service acct', cls: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200' };
    case 'IDENTITY_ASSERTION_A2A_SERVER':
      return { label: 'A2A Server', cls: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' };
    default:
      return { label: type, cls: 'bg-gray-100 text-gray-800' };
  }
}


// ---------------------------------------------------------------------------
// Connections & Resources Tab
// ---------------------------------------------------------------------------
function ConnectionsTab({
  detail,
  agentId,
  onRefresh,
}: {
  detail: AgentDetail | null;
  agentId: string;
  onRefresh: () => void;
}) {
  const { toast } = useToast();
  const [filter, setFilter] = useState<ConnectionFilter>('all');
  const [editTarget, setEditTarget] = useState<{ conn: AgentConnection; resourceName: string } | null>(null);
  const [configureTarget, setConfigureTarget] = useState<AgentConnection | null>(null);
  const [mergeSource, setMergeSource] = useState<string | null>(null);
  const [detaching, setDetaching] = useState<string | null>(null);

  const handleDetach = async (conn: AgentConnection) => {
    if (!conn.resource) return;
    if (
      !confirm(
        `Detach connection ${conn.connection_id} from resource '${conn.resource.name}'? ` +
          `Sync state is preserved but this connection stops routing until linked again.`,
      )
    ) {
      return;
    }
    setDetaching(conn.connection_id);
    try {
      await apiClient.unlinkResourceFromConnection(agentId, conn.connection_id);
      toast({
        title: 'Detached',
        description: `Connection ${conn.connection_id} detached from ${conn.resource.name}.`,
      });
      onRefresh();
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to detach connection',
        variant: 'destructive',
      });
    } finally {
      setDetaching(null);
    }
  };

  // --- Empty states ---
  if (!detail) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Server className="w-10 h-10 mb-3 text-muted-foreground/40" />
        <p className="font-medium text-muted-foreground">No connection data available</p>
        <p className="text-sm text-muted-foreground mt-1 max-w-md">
          This agent was created manually and does not have Okta Managed
          Connections. To use Okta-native resource management, import the
          agent from Okta via the Okta Import page.
        </p>
      </div>
    );
  }

  if (detail.connections.length === 0) {
    const agentName = detail.agent.agent_id;
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Server className="w-10 h-10 mb-3 text-muted-foreground/40" />
        <p className="font-medium text-muted-foreground">No Managed Connections found</p>
        <p className="text-sm text-muted-foreground mt-1 max-w-lg">
          Add connections in the Okta Admin Console under Directory &rarr; AI
          Agents &rarr; <span className="font-medium">{agentName}</span> &rarr;
          Managed Connections, then click &ldquo;Sync from Okta&rdquo;.
        </p>
      </div>
    );
  }

  // --- Filtering ---
  const connections = detail.connections;
  const hasResource = (c: AgentConnection) =>
    !!(c.resource && c.resource.enabled !== false && c.resource.url);
  const isSupported = (c: AgentConnection) => SUPPORTED_CONNECTION_TYPES.includes(c.connection_type);
  const activeCount = connections.filter((c) => c.status === 'ACTIVE').length;
  const unlinkedCount = connections.filter((c) => !hasResource(c) && c.status === 'ACTIVE' && isSupported(c)).length;
  const inactiveCount = connections.filter((c) => c.status !== 'ACTIVE').length;
  const xaaCount = connections.filter((c) => c.connection_type === 'IDENTITY_ASSERTION_CUSTOM_AS').length;
  const secretCount = connections.filter((c) => c.connection_type === 'STS_VAULT_SECRET' || c.connection_type === 'SECRET').length;
  const saCount = connections.filter((c) => c.connection_type === 'STS_SERVICE_ACCOUNT' || c.connection_type === 'SERVICE_ACCOUNT').length;

  const filtered = connections.filter((c) => {
    switch (filter) {
      case 'active': return c.status === 'ACTIVE';
      case 'unlinked': return !hasResource(c) && c.status === 'ACTIVE' && isSupported(c);
      case 'inactive': return c.status !== 'ACTIVE';
      case 'XAA': return c.connection_type === 'IDENTITY_ASSERTION_CUSTOM_AS';
      case 'SECRET': return c.connection_type === 'STS_VAULT_SECRET' || c.connection_type === 'SECRET';
      case 'SERVICE_ACCOUNT': return c.connection_type === 'STS_SERVICE_ACCOUNT' || c.connection_type === 'SERVICE_ACCOUNT';
      default: return true;
    }
  });

  // Sort: active first, inactive last
  const sorted = [...filtered].sort((a, b) => {
    if (a.status === 'ACTIVE' && b.status !== 'ACTIVE') return -1;
    if (a.status !== 'ACTIVE' && b.status === 'ACTIVE') return 1;
    return 0;
  });

  const filters: { id: ConnectionFilter; label: string; count: number }[] = [
    { id: 'all', label: 'All', count: connections.length },
    { id: 'active', label: 'Active', count: activeCount },
    ...(unlinkedCount > 0 ? [{ id: 'unlinked' as const, label: 'Needs resource', count: unlinkedCount }] : []),
    ...(inactiveCount > 0 ? [{ id: 'inactive' as const, label: 'Inactive', count: inactiveCount }] : []),
    ...(xaaCount > 0 ? [{ id: 'XAA' as const, label: 'XAA', count: xaaCount }] : []),
    ...(secretCount > 0 ? [{ id: 'SECRET' as const, label: 'Secret', count: secretCount }] : []),
    ...(saCount > 0 ? [{ id: 'SERVICE_ACCOUNT' as const, label: 'Service acct', count: saCount }] : []),
  ];

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="flex flex-wrap gap-2">
        {filters.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              filter === f.id
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            {f.label}
            <span className={`tabular-nums ${filter === f.id ? 'text-primary-foreground/80' : 'text-muted-foreground/60'}`}>
              {f.count}
            </span>
          </button>
        ))}
      </div>

      {/* Connection cards */}
      {sorted.map((conn) => {
        const resource = conn.resource;
        const hasEnabled = !!(resource && resource.enabled !== false && resource.url);
        const isActive = conn.status === 'ACTIVE';
        const isInactive = !isActive;
        const isNotSupported = !isSupported(conn);
        const typeBadge = connTypeBadge(conn.connection_type);

        return (
          <Card
            key={conn.connection_id}
            className={`transition-colors ${
              isInactive || isNotSupported
                ? 'opacity-60 bg-muted/30'
                : !hasEnabled
                  ? 'border-l-[3px] border-l-amber-400 bg-amber-50/40 dark:bg-amber-950/20'
                  : 'hover:bg-muted/20'
            }`}
          >
            <CardContent className="p-5 space-y-4">
              {/* --- Connection header --- */}
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1.5 min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-[10px] font-semibold tracking-wider uppercase text-muted-foreground">
                      Okta managed connection
                    </p>
                    {!hasEnabled && isActive && !isNotSupported && (
                      <span className="text-[10px] font-semibold tracking-wider uppercase text-amber-600 dark:text-amber-400">
                        &mdash; needs URL &amp; enable
                      </span>
                    )}
                    {isInactive && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                        INACTIVE
                      </span>
                    )}
                  </div>
                  <p className="font-semibold text-sm leading-tight">{conn.display_name}</p>
                  <div className="space-y-0.5 pt-0.5">
                    {conn.resource_id && (
                      <p className="text-xs text-muted-foreground">
                        <span className="font-medium text-foreground/70">Auth Server:</span>{' '}
                        <span className="font-mono">
                          {conn.auth_server_name ? `${conn.auth_server_name} (${conn.resource_id})` : conn.resource_id}
                        </span>
                      </p>
                    )}
                    {conn.resource_indicator && (
                      <p className="text-xs text-muted-foreground break-all">
                        <span className="font-medium text-foreground/70">Audience:</span>{' '}
                        <span className="font-mono">{conn.resource_indicator}</span>
                      </p>
                    )}
                    {!conn.resource_id && !conn.resource_indicator && (
                      <p className="text-xs font-mono text-muted-foreground">{conn.connection_id}</p>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-1.5 pt-1">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${typeBadge.cls}`}>
                      {typeBadge.label}
                    </span>
                    {conn.scope_condition && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200">
                        {conn.scope_condition}
                      </span>
                    )}
                  </div>

                  {conn.scopes.length > 0 && (
                    <div className="flex flex-wrap gap-1 pt-1">
                      {conn.scopes.map((scope) => (
                        <span
                          key={scope}
                          className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300 font-mono"
                        >
                          {scope}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {isInactive && (
                <div className="p-3 rounded-lg bg-gray-100 dark:bg-gray-800/50 border text-sm text-muted-foreground">
                  This connection is inactive in Okta. Activate it in the Okta Admin Console to enable routing.
                </div>
              )}

              {isNotSupported && (
                <div className="p-3 rounded-lg bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 text-sm text-orange-800 dark:text-orange-300">
                  Connection type <span className="font-mono font-medium">{conn.connection_type}</span> is not currently supported by this adapter. Routing through this connection is disabled.
                </div>
              )}

              {/* --- Resource row (one per connection) --- */}
              {isActive && resource && (
                <div className="rounded-lg bg-muted/60 border p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <Server className={`w-4 h-4 flex-shrink-0 ${hasEnabled ? 'text-green-600' : 'text-amber-500'}`} />
                      <p className="font-semibold text-sm truncate">{resource.name}</p>
                      {resource.enabled === false && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                          DISABLED
                        </span>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs px-2"
                        onClick={() => setEditTarget({ conn, resourceName: resource.name })}
                      >
                        <Edit className="w-3 h-3 mr-1" /> Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs px-2"
                        onClick={() => setMergeSource(resource.name)}
                        title="Merge this resource into another master"
                        disabled={!resource.url}
                      >
                        <GitMerge className="w-3 h-3 mr-1" /> Merge
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs px-2"
                        onClick={() => handleDetach(conn)}
                        disabled={detaching === conn.connection_id}
                        title="Detach this connection from its master (sync state preserved)"
                      >
                        {detaching === conn.connection_id ? (
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        ) : (
                          <Link2Off className="w-3 h-3 mr-1" />
                        )}
                        Detach
                      </Button>
                      {conn.connection_type === 'STS_ACCESS_TOKEN' && (
                        <CopyPreConsentResourceButton agentId={agentId} resourceName={resource.name} />
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Globe className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate font-mono">
                      {resource.url || <span className="italic">URL not set — click Edit</span>}
                    </span>
                  </div>
                  {resource.auth_method && (
                    <p className="text-xs text-muted-foreground">
                      Auth: <code className="font-mono">{resource.auth_method}</code>
                    </p>
                  )}
                </div>
              )}

              {/* --- Unlinked connection CTA --- */}
              {isActive && !resource && !isNotSupported && (
                <div className="rounded-lg bg-amber-50/40 dark:bg-amber-950/20 border border-amber-300 p-3 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <Link2Off className="w-4 h-4 flex-shrink-0 text-amber-600" />
                      <p className="text-sm">
                        This connection is not linked to a resource — requests via this connection will not route until you link or create one.
                      </p>
                    </div>
                    <Button
                      variant="default"
                      size="sm"
                      className="h-7 text-xs px-2 flex-shrink-0"
                      onClick={() => setConfigureTarget(conn)}
                    >
                      <Plus className="w-3 h-3 mr-1" /> Configure resource
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {sorted.length === 0 && (
        <div className="text-center py-8 text-muted-foreground text-sm">
          No connections match the selected filter.
        </div>
      )}

      {/* Edit resource dialog */}
      {editTarget && (
        <EditConnectionResourceDialog
          resourceName={editTarget.resourceName}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); onRefresh(); }}
        />
      )}

      {/* Configure unlinked connection dialog */}
      {configureTarget && (
        <ConfigureConnectionResourceDialog
          agentId={agentId}
          conn={configureTarget}
          onClose={() => setConfigureTarget(null)}
          onSaved={() => { setConfigureTarget(null); onRefresh(); }}
        />
      )}

      {/* Merge resource dialog */}
      {mergeSource && (
        <MergeResourcesDialog
          source={mergeSource}
          open={true}
          onOpenChange={(open) => { if (!open) setMergeSource(null); }}
          onMerged={() => { setMergeSource(null); onRefresh(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Edit Resource Dialog (admin-editable fields only)
// ---------------------------------------------------------------------------
function EditConnectionResourceDialog({
  resourceName,
  onClose,
  onSaved,
}: {
  resourceName: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<{
    name: string;
    url: string;
    type: string;
    description: string;
    enabled: boolean;
  }>({
    name: resourceName,
    url: '',
    type: 'mcp',
    description: '',
    enabled: false,
  });

  useEffect(() => {
    (async () => {
      try {
        const r = await apiClient.getResource(resourceName);
        setForm({
          name: r.name,
          url: r.url || r.mcp_url || '',
          type: r.type || r.protocol || 'mcp',
          description: r.description || '',
          enabled: r.enabled !== false,
        });
      } catch (e: any) {
        toast({ title: 'Error', description: e.message || 'Failed to load resource', variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    })();
  }, [resourceName, toast]);

  const handleSave = async () => {
    if (!form.url) {
      toast({ title: 'Validation Error', description: 'URL is required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await apiClient.updateResource(resourceName, {
        name: form.name,
        url: form.url,
        type: form.type,
        description: form.description,
        enabled: form.enabled,
      });
      toast({ title: 'Saved', description: 'Resource updated' });
      onSaved();
    } catch (e: any) {
      toast({ title: 'Error', description: e.message || 'Failed to save', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit resource</DialogTitle>
          <DialogDescription>
            Change routing fields. Sync-owned fields (scopes, connection type) are managed in Okta.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="er-name">Name (slug) *</Label>
              <Input
                id="er-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="er-url">URL *</Label>
              <Input
                id="er-url"
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                placeholder="https://mcp.example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="er-type">Type</Label>
              <select
                id="er-type"
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="mcp">MCP</option>
                <option value="api">API</option>
                <option value="a2a">A2A</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="er-desc">Description</Label>
              <Input
                id="er-desc"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                className="h-4 w-4"
              />
              <span>Enabled</span>
            </label>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || loading}>
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Configure Connection Resource Dialog (link existing OR create new)
// ---------------------------------------------------------------------------
function ConfigureConnectionResourceDialog({
  agentId,
  conn,
  onClose,
  onSaved,
}: {
  agentId: string;
  conn: AgentConnection;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { toast } = useToast();
  const [mode, setMode] = useState<'use_existing' | 'create_new'>('use_existing');
  const [resources, setResources] = useState<{ name: string; url?: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // use_existing form
  const [pickedResource, setPickedResource] = useState('');

  // create_new form
  const [form, setForm] = useState({
    name: slugify(conn.display_name || conn.connection_id),
    url: '',
    type: 'mcp',
    description: '',
    enabled: false,
  });

  useEffect(() => {
    (async () => {
      try {
        const list = await apiClient.listResources();
        setResources(
          (list || []).map((r) => ({ name: r.name, url: r.url || r.mcp_url || '' })),
        );
      } catch (e: any) {
        toast({
          title: 'Error',
          description: e.message || 'Failed to load resources',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [toast]);

  const eligibleExisting = resources.filter((r) => !!r.url);

  const handleUseExisting = async () => {
    if (!pickedResource) {
      toast({
        title: 'Validation Error',
        description: 'Pick a resource to link',
        variant: 'destructive',
      });
      return;
    }
    setSaving(true);
    try {
      await apiClient.linkResourceToConnection(agentId, conn.connection_id, pickedResource);
      toast({
        title: 'Linked',
        description: `Connection ${conn.connection_id} now routes to ${pickedResource}.`,
      });
      onSaved();
    } catch (e: any) {
      toast({
        title: e.status === 409 ? 'Already linked' : 'Error',
        description: e.message || 'Failed to link connection',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCreateNew = async () => {
    const name = form.name.trim();
    if (!name || !SLUG_PATTERN.test(name)) {
      toast({
        title: 'Validation Error',
        description: 'Name must be a valid slug (a-z, 0-9, hyphens)',
        variant: 'destructive',
      });
      return;
    }
    if (!form.url.trim()) {
      toast({
        title: 'Validation Error',
        description: 'URL is required',
        variant: 'destructive',
      });
      return;
    }
    setSaving(true);
    try {
      await apiClient.createResource({
        name,
        type: form.type,
        url: form.url.trim(),
        description: form.description.trim(),
        enabled: form.enabled,
      });
      try {
        await apiClient.linkResourceToConnection(agentId, conn.connection_id, name);
      } catch (linkErr: any) {
        toast({
          title: 'Created but link failed',
          description: `Master '${name}' created. Linking failed: ${linkErr.message || linkErr}`,
          variant: 'destructive',
        });
        return;
      }
      toast({
        title: 'Created and linked',
        description: `Connection ${conn.connection_id} now routes to ${name}.`,
      });
      onSaved();
    } catch (e: any) {
      toast({
        title: e.status === 409 ? 'Name already exists' : 'Error',
        description: e.message || 'Failed to create resource',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Configure resource for connection</DialogTitle>
          <DialogDescription>
            Link this Okta Managed Connection ({conn.display_name || conn.connection_id}) to an existing master, or create a new one.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 border-b">
          <button
            type="button"
            onClick={() => setMode('use_existing')}
            className={`pb-2 text-sm font-medium border-b-2 transition-colors ${
              mode === 'use_existing'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Use existing resource
          </button>
          <button
            type="button"
            onClick={() => setMode('create_new')}
            className={`pb-2 text-sm font-medium border-b-2 transition-colors ${
              mode === 'create_new'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Create new resource
          </button>
        </div>

        {loading ? (
          <div className="py-8 flex justify-center">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : mode === 'use_existing' ? (
          <div className="space-y-3 pt-2">
            {eligibleExisting.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">
                No existing resources have a URL configured. Use "Create new resource" instead.
              </p>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="cc-pick">Resource</Label>
                <select
                  id="cc-pick"
                  value={pickedResource}
                  onChange={(e) => setPickedResource(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Select a resource…</option>
                  {eligibleExisting.map((r) => (
                    <option key={r.name} value={r.name}>
                      {r.name} ({r.url})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground">
                  Empty masters (no URL) are excluded — they aren't routable yet.
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor="cc-name">Name (slug) *</Label>
              <Input
                id="cc-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Lowercase letters, digits, and hyphens. Globally unique.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cc-url">URL *</Label>
              <Input
                id="cc-url"
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                placeholder="https://mcp.example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cc-type">Type</Label>
              <select
                id="cc-type"
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="mcp">MCP</option>
                <option value="api">API</option>
                <option value="a2a">A2A</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cc-desc">Description</Label>
              <Input
                id="cc-desc"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                className="h-4 w-4"
              />
              <span>Enabled</span>
            </label>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {mode === 'use_existing' ? (
            <Button
              onClick={handleUseExisting}
              disabled={saving || loading || !pickedResource}
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Link2 className="w-4 h-4 mr-1" />
              Link
            </Button>
          ) : (
            <Button onClick={handleCreateNew} disabled={saving || loading}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Plus className="w-4 h-4 mr-1" />
              Create &amp; link
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Create Resource Dialog
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Configuration Tab
// ---------------------------------------------------------------------------
function ConfigurationTab({ agent, onSaved, publicConfig }: { agent: Agent; onSaved: () => void; publicConfig: PublicConfig | null }) {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [generatingKey, setGeneratingKey] = useState(false);
  const [fetchingSecret, setFetchingSecret] = useState(false);
  const [credStatus, setCredStatus] = useState<CredentialStatus | null>(null);
  const agentRoutingEnabled = publicConfig?.agent_routing_enabled ?? false;

  const [credStatusError, setCredStatusError] = useState<string | null>(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [form, setForm] = useState({
    cimd_client_id: agent.cimd_client_id_url || '',
    client_secret: '',
    private_key: '',
    enabled: agent.enabled,
    client_mode: agent.dcr_selectable ? 'dcr' : (agent.cimd_client_id_url ? 'cimd' : 'none'),
    dcr_redirect_uris: agent.dcr_redirect_uris || [],
  });
  const [dcrUriErrors, setDcrUriErrors] = useState<Record<number, string | undefined>>({});

  const updateDcrUri = (index: number, value: string) => {
    setForm((prev) => {
      const next = [...prev.dcr_redirect_uris];
      next[index] = value;
      return { ...prev, dcr_redirect_uris: next };
    });
    if (value.trim() === '') {
      setDcrUriErrors((prev) => ({ ...prev, [index]: 'Redirect URI must not be empty' }));
      return;
    }
    try {
      validateRedirectPattern(value.trim());
      setDcrUriErrors((prev) => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
    } catch (e) {
      setDcrUriErrors((prev) => ({
        ...prev,
        [index]: e instanceof RedirectPatternError ? e.message : 'Invalid redirect URI',
      }));
    }
  };

  const addDcrUri = () => {
    setForm((prev) => ({ ...prev, dcr_redirect_uris: [...prev.dcr_redirect_uris, ''] }));
  };

  const removeDcrUri = (index: number) => {
    setForm((prev) => ({
      ...prev,
      dcr_redirect_uris: prev.dcr_redirect_uris.filter((_, i) => i !== index),
    }));
    setDcrUriErrors((prev) => {
      const next: Record<number, string | undefined> = {};
      Object.entries(prev).forEach(([k, v]) => {
        const ki = Number(k);
        if (ki < index) next[ki] = v;
        else if (ki > index) next[ki - 1] = v;
      });
      return next;
    });
  };

  const hasDcrUriErrors = Object.values(dcrUriErrors).some(Boolean);

  const loadCredentialStatus = useCallback(async () => {
    try {
      setCredStatusError(null);
      const status = await apiClient.getCredentialStatus(agent.agent_id);
      setCredStatus(status);
    } catch (err: any) {
      if (err.status === 404) {
        setCredStatus(null);
      } else {
        setCredStatusError(err.message || 'Failed to load credential status');
      }
    }
  }, [agent.agent_id]);

  useEffect(() => {
    loadCredentialStatus();
  }, [loadCredentialStatus]);

  const [delegationCandidates, setDelegationCandidates] = useState<DelegationAppCandidate[]>([]);
  const [delegationLoading, setDelegationLoading] = useState(false);
  const [delegationError, setDelegationError] = useState<string | null>(null);
  const [selectedDelegationApp, setSelectedDelegationApp] = useState<string | null>(
    agent.selected_delegation_app_id || null
  );
  const [savingDelegation, setSavingDelegation] = useState(false);

  const loadDelegationCandidates = useCallback(async () => {
    if (!agent.okta_ai_agent_id) return;
    setDelegationLoading(true);
    setDelegationError(null);
    try {
      const cands = await apiClient.listDelegationApps(agent.okta_ai_agent_id);
      setDelegationCandidates(cands);
    } catch (err: any) {
      setDelegationError(err.message || 'Failed to load linked application candidates');
    } finally {
      setDelegationLoading(false);
    }
  }, [agent.okta_ai_agent_id]);

  useEffect(() => {
    loadDelegationCandidates();
  }, [loadDelegationCandidates]);

  useEffect(() => {
    setSelectedDelegationApp(agent.selected_delegation_app_id || null);
  }, [agent.selected_delegation_app_id]);

  const handleSaveDelegationSelection = async () => {
    if (!selectedDelegationApp) return;
    const match = delegationCandidates.find((c) => c.app_instance_id === selectedDelegationApp);
    setSavingDelegation(true);
    try {
      await apiClient.updateAgent(agent.agent_id, {
        selected_delegation_app_id: selectedDelegationApp,
        effective_client_id: match?.client_id ?? undefined,
      });
      toast({ title: 'Saved', description: 'Linked application updated' });
      onSaved();
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSavingDelegation(false);
    }
  };

  const handleFetchSecret = async () => {
    setFetchingSecret(true);
    try {
      const result = await apiClient.fetchClientSecret(agent.agent_id);
      const msg = result.source === 'fetched_existing'
        ? 'Client secret fetched from Okta'
        : 'New client secret generated in Okta';
      toast({ title: 'Success', description: msg });
      await loadCredentialStatus();
      onSaved();
    } catch (err: any) {
      if (err.status === 401) {
        toast({ title: 'Session Expired', description: 'Your Okta session has expired. Please log in again.', variant: 'destructive' });
      } else if (err.status === 403) {
        toast({ title: 'Insufficient Permissions', description: 'Your Okta admin role does not allow client secret retrieval for this app.', variant: 'destructive' });
      } else {
        toast({ title: 'Error', description: err.message, variant: 'destructive' });
      }
    } finally {
      setFetchingSecret(false);
    }
  };

  const handleGenerateKey = async (mode: 'push' | 'jwks_uri') => {
    setGeneratingKey(true);
    try {
      const result = await apiClient.generateKeyPair(agent.agent_id, mode);
      toast({ title: 'Success', description: `Key pair generated and registered (kid: ${result.kid})` });
      await loadCredentialStatus();
      onSaved();
    } catch (err: any) {
      if (err.status === 401) {
        toast({ title: 'Session Expired', description: 'Your Okta session has expired. Please log in again.', variant: 'destructive' });
      } else if (err.status === 403) {
        toast({ title: 'Insufficient Permissions', description: err.message, variant: 'destructive' });
      } else {
        toast({ title: 'Error', description: err.message, variant: 'destructive' });
      }
    } finally {
      setGeneratingKey(false);
    }
  };

  const handleSave = async () => {
    const updates: Record<string, any> = {};
    if (form.client_mode === 'cimd') {
      const cimd = form.cimd_client_id.trim();
      updates.cimd_client_id = cimd || null;
      updates.dcr_selectable = false;
    } else if (form.client_mode === 'dcr') {
      updates.cimd_client_id = null;
      updates.dcr_selectable = true;
      try {
        updates.dcr_redirect_uris = validateRedirectPatterns(form.dcr_redirect_uris);
      } catch (e) {
        toast({
          title: 'Validation Error',
          description: e instanceof RedirectPatternError ? e.message : 'Invalid redirect URI',
          variant: 'destructive',
        });
        return;
      }
    } else {
      updates.cimd_client_id = null;
      updates.dcr_selectable = false;
    }

    setSaving(true);
    try {
      if (form.client_secret) updates.client_secret = form.client_secret;
      if (form.private_key) updates.private_key = form.private_key;
      updates.enabled = form.enabled;

      await apiClient.updateAgent(agent.agent_id, updates);
      toast({ title: 'Saved', description: `Configuration updated for ${agent.agent_id}` });
      setForm({ ...form, client_secret: '', private_key: '' });
      await loadCredentialStatus();
      onSaved();
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6">
      {/* Read-only identity */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {agent.registration_source === 'okta-import' ? 'Okta Identity (read-only)' : 'Identity'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Agent Name:</span>
              <span className="ml-2 font-mono">{agent.agent_id}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Client ID:</span>
              <span className="ml-2 font-mono text-xs">{agent.client_id}</span>
            </div>
            {agent.okta_ai_agent_id && (
              <div className="col-span-2">
                <span className="text-muted-foreground">Okta Agent ID:</span>
                <span className="ml-2 font-mono text-xs">{agent.okta_ai_agent_id}</span>
              </div>
            )}
            {agent.principal_id && (
              <div className="col-span-2">
                <span className="text-muted-foreground">Principal ID:</span>
                <span className="ml-2 font-mono text-xs break-all">{agent.principal_id}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Linked Application */}
      {agent.okta_ai_agent_id && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Linked Application</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {delegationLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading linked application candidates...
              </div>
            ) : delegationError ? (
              <p className="text-sm text-muted-foreground">{delegationError}</p>
            ) : delegationCandidates.length <= 1 ? (
              <p className="text-sm text-muted-foreground">
                {delegationCandidates.length === 1
                  ? `${delegationCandidates[0].label ?? '(no label)'} (${delegationCandidates[0].app_instance_id})`
                  : 'No linked application candidates found.'}
              </p>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Choose which OIDC application this agent's OAuth identity binds to.
                </p>
                <div className="space-y-1">
                  {delegationCandidates.map((c) => (
                    <label key={c.app_instance_id} className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name="linked-app-selection"
                        checked={selectedDelegationApp === c.app_instance_id}
                        onChange={() => setSelectedDelegationApp(c.app_instance_id)}
                      />
                      <span>
                        {c.label ?? '(no label)'} &mdash; <code className="text-xs">{c.app_instance_id}</code>
                      </span>
                    </label>
                  ))}
                </div>
                <Button
                  size="sm"
                  onClick={handleSaveDelegationSelection}
                  disabled={
                    savingDelegation ||
                    !selectedDelegationApp ||
                    selectedDelegationApp === (agent.selected_delegation_app_id || null)
                  }
                >
                  {savingDelegation ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : null}
                  Save Selection
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Credential Status & Automation */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Credential Status</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {credStatusError ? (
            <p className="text-sm text-muted-foreground">{credStatusError}</p>
          ) : credStatus ? (
            (() => {
              const authMethod = credStatus.token_endpoint_auth_method;
              const isSecretAuth = authMethod === 'client_secret_basic' || authMethod === 'client_secret_post';
              const isKeyAuth = authMethod === 'private_key_jwt';
              const isUnresolved = !isSecretAuth && !isKeyAuth;

              return (
                <>
                  <div className="space-y-2 text-sm">
                    {(isSecretAuth || isUnresolved) && (
                      <div className="flex items-center gap-2">
                        {credStatus.has_client_secret ? (
                          <Check className="w-4 h-4 text-green-600" />
                        ) : (
                          <X className="w-4 h-4 text-red-500" />
                        )}
                        <span>
                          Client Secret: {credStatus.has_client_secret ? 'Configured' : 'Not Configured'}
                        </span>
                      </div>
                    )}
                    {(isKeyAuth || isUnresolved) && (
                      <div className="flex items-center gap-2">
                        {credStatus.has_private_key ? (
                          <Check className="w-4 h-4 text-green-600" />
                        ) : (
                          <X className="w-4 h-4 text-red-500" />
                        )}
                        <span>
                          Signing Key: {credStatus.has_private_key ? 'Configured' : 'Not Configured'}
                        </span>
                      </div>
                    )}
                    {credStatus.registered_keys.length > 0 && (
                      <div className="ml-6 space-y-1">
                        <span className="text-muted-foreground text-xs">Registered with Okta:</span>
                        {credStatus.registered_keys.map((k) => (
                          <div key={k.kid} className="flex items-center gap-1 text-xs font-mono text-muted-foreground">
                            <Key className="w-3 h-3" />
                            <span>kid: {k.kid}</span>
                            <span className="text-muted-foreground/60">({k.alg})</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {credStatus.token_endpoint_auth_method && (
                      <div className="text-xs text-muted-foreground">
                        Auth method: {credStatus.token_endpoint_auth_method}
                      </div>
                    )}
                    {(isKeyAuth || isUnresolved) && credStatus.app_key_management_mode && (
                      <div className="text-xs text-muted-foreground">
                        Keys managed on: {credStatus.key_target === 'app' ? 'Application' : 'Agent'}
                      </div>
                    )}
                    {(isKeyAuth || isUnresolved) && credStatus.has_private_key && (
                      <div className="text-xs text-muted-foreground">
                        Key delivery: {credStatus.agent_key_delivery_mode === 'jwks_uri'
                          ? 'Dynamic (JWKS URI)'
                          : 'Static (Pushed)'}
                      </div>
                    )}
                  </div>

                  {agent.okta_ai_agent_id ? (
                    <div className="space-y-2 pt-2">
                      {isUnresolved && (
                        <p className="text-xs text-muted-foreground">
                          Auth method not yet resolved for this agent's linked application — showing all
                          credential actions. Re-sync the agent to resolve it and narrow this list.
                        </p>
                      )}
                      <div className="flex flex-wrap gap-2">
                        {(isSecretAuth || isUnresolved) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleFetchSecret}
                            disabled={fetchingSecret}
                            title="Fetches an existing active client secret from Okta, or generates a new one if none exists."
                          >
                            {fetchingSecret ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <RefreshCw className="w-4 h-4 mr-2" />
                            )}
                            Fetch Secret from Okta
                          </Button>
                        )}
                        {(isKeyAuth || isUnresolved) && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleGenerateKey('push')}
                              disabled={generatingKey}
                              title="Uploads the key directly to Okta. Works in any environment, including local or private deployments where this bridge isn't publicly reachable."
                            >
                              {generatingKey ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <Key className="w-4 h-4 mr-2" />
                              )}
                              Push Public Key
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleGenerateKey('jwks_uri')}
                              disabled={generatingKey}
                              title="Okta fetches the key from this bridge's own URL. Requires GATEWAY_BASE_URL to be publicly reachable over HTTPS."
                            >
                              {generatingKey ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <Key className="w-4 h-4 mr-2" />
                              )}
                              Use Dynamic JWKS URL
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Link an Okta AI Agent ID to enable automated credential provisioning.
                    </p>
                  )}
                </>
              );
            })()
          ) : (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading credential status...
            </div>
          )}
        </CardContent>
      </Card>

      {/* Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Client Mode</Label>
            <div className="space-y-2">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="client_mode"
                  value="none"
                  checked={form.client_mode === 'none'}
                  onChange={() => setForm({ ...form, client_mode: 'none' })}
                  className="rounded"
                />
                <span className="text-sm">Standard</span>
                <span className="text-xs text-muted-foreground">- No CIMD or DCR</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="client_mode"
                  value="cimd"
                  checked={form.client_mode === 'cimd'}
                  onChange={() => setForm({ ...form, client_mode: 'cimd' })}
                  className="rounded"
                />
                <span className="text-sm">CIMD Client</span>
                <span className="text-xs text-muted-foreground">- URL-based client identity</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="client_mode"
                  value="dcr"
                  checked={form.client_mode === 'dcr'}
                  onChange={() => setForm({ ...form, client_mode: 'dcr' })}
                  className="rounded"
                />
                <span className="text-sm">DCR Enabled</span>
                <span className="text-xs text-muted-foreground">- Share DCR credentials</span>
              </label>
            </div>
          </div>

          {form.client_mode === 'none' && (
            <div className="space-y-2 pl-6 border-l-2 border-muted">
              <Label>Client Secret Proxy</Label>
              <p className="text-xs text-muted-foreground">
                Compatibility shim for clients (e.g. Gemini) that don&apos;t support DCR
                or CIMD. The gateway issues a static secret bound to this agent&apos;s
                client_id and substitutes the real Okta client_secret upstream.
              </p>
              <ClientSecretProxySection agentId={agent.agent_id} />
            </div>
          )}

          {form.client_mode === 'cimd' && (
            <div className="space-y-1 pl-6 border-l-2 border-muted">
              <Label htmlFor="cfg-cimd">CIMD Client ID</Label>
              <Input
                id="cfg-cimd"
                value={form.cimd_client_id}
                onChange={(e) => setForm({ ...form, cimd_client_id: e.target.value })}
                placeholder="https://claude.ai/oauth/client-metadata.json"
              />
              <p className="text-xs text-muted-foreground">
                URL-based client identifier for CIMD-enabled MCP clients.
              </p>
            </div>
          )}
          {/* Allow DCR registration if agent_routing_enabled is true */}
          {agentRoutingEnabled ? (
            form.client_mode === 'dcr' && (
              <div className="space-y-2 pl-6 border-l-2 border-muted">
                <Label>DCR Redirect URIs</Label>
                <p className="text-xs text-muted-foreground">
                  Redirect URIs (or patterns) accepted when this agent is the target
                  of a Dynamic Client Registration request.
                </p>
                {form.dcr_redirect_uris.map((uri, index) => {
                  const error = dcrUriErrors[index];
                  return (
                    <div key={index} className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Label htmlFor={`cfg-dcr-uri-${index}`} className="sr-only">
                          Redirect URI {index + 1}
                        </Label>
                        <Input
                          id={`cfg-dcr-uri-${index}`}
                          value={uri}
                          onChange={(e) => updateDcrUri(index, e.target.value)}
                          placeholder="http://localhost:*/callback"
                          className={error ? 'border-red-500 focus-visible:ring-red-500' : ''}
                          aria-invalid={!!error}
                        />
                        <Button
                          variant="destructive"
                          size="icon"
                          onClick={() => removeDcrUri(index)}
                          title={`Remove redirect URI ${index + 1}`}
                          className="flex-shrink-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      {error && (
                        <p className="text-xs text-red-500 ml-1" role="alert">
                          {error}
                        </p>
                      )}
                    </div>
                  );
                })}
                <Button variant="outline" size="sm" onClick={addDcrUri}>
                  <Plus className="w-4 h-4 mr-1.5" />
                  Add redirect URI
                </Button>
              </div>
            )
          ) : null}

          <div className="flex items-center space-x-2 pt-2">
            <input
              id="cfg-enabled"
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
              className="rounded"
            />
            <Label htmlFor="cfg-enabled">Agent Enabled</Label>
            <span className="text-xs text-muted-foreground">(Controls whether this agent can process requests)</span>
          </div>

          <div className="pt-2">
            <Button
              onClick={handleSave}
              disabled={saving || (form.client_mode === 'dcr' && hasDcrUriErrors)}
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Settings
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Manual credential entry (collapsible) */}
      <Card>
        <CardHeader>
          <button
            type="button"
            className="flex items-center gap-2 w-full text-left"
            onClick={() => setManualOpen(!manualOpen)}
          >
            {manualOpen ? (
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            )}
            <CardTitle className="text-base">Manual Configuration</CardTitle>
          </button>
          <p className="text-xs text-muted-foreground ml-6">
            Directly enter credentials when automated provisioning is not available.
          </p>
        </CardHeader>
        {manualOpen && (
          <CardContent className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="cfg-secret">Client Secret</Label>
              <Input
                id="cfg-secret"
                type="password"
                value={form.client_secret}
                onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
                placeholder="Leave blank to keep existing value"
              />
              <p className="text-xs text-muted-foreground">
                Okta OIDC app&apos;s client_secret. Stored encrypted.
              </p>
            </div>

            <div className="space-y-1">
              <Label htmlFor="cfg-pk">Private Key (JWK)</Label>
              <textarea
                id="cfg-pk"
                value={form.private_key}
                onChange={(e) => setForm({ ...form, private_key: e.target.value })}
                placeholder="Leave blank to keep existing value"
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 font-mono h-24 resize-y"
              />
              <p className="text-xs text-muted-foreground">
                JWK private key from the Okta AI Agent config. Used for ID-JAG token signing.
              </p>
            </div>

            <div className="pt-2">
              <Button onClick={handleSave} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Save Manual Credentials
              </Button>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Pre-Consent Link */}
      <PreConsentLinkSection agentId={agent.agent_id} publicConfig={publicConfig} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pre-Consent Link Section
// ---------------------------------------------------------------------------
function PreConsentLinkSection({ agentId, publicConfig }: { agentId: string; publicConfig: PublicConfig | null }) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const gatewayBaseUrl = publicConfig?.gateway_base_url?.replace(/\/$/, '') ?? null;

  const preConsentUrl = gatewayBaseUrl
    ? `${gatewayBaseUrl}/oauth/pre-consent?agent_id=${encodeURIComponent(agentId)}`
    : '';

  const handleCopy = async () => {
    if (!preConsentUrl) return;
    try {
      await navigator.clipboard.writeText(preConsentUrl);
      setCopied(true);
      toast({ title: 'Copied!', description: 'Pre-consent link copied to clipboard.' });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: 'Error', description: 'Failed to copy to clipboard.', variant: 'destructive' });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Pre-Consent Link</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2">
          <Input
            readOnly
            value={preConsentUrl}
            placeholder={gatewayBaseUrl === null ? 'Loading gateway URL…' : ''}
            className="font-mono text-xs flex-1"
            onClick={(e) => (e.target as HTMLInputElement).select()}
          />
          <Button variant="outline" size="sm" onClick={handleCopy} disabled={!preConsentUrl}>
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            <span className="ml-1.5">{copied ? 'Copied' : 'Copy link'}</span>
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Send this link to a user to let them re-verify STS-managed connections without
          restarting their MCP client. The user will authenticate against this agent&apos;s
          Okta app and land on the consent interstitial.
        </p>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Per-resource pre-consent copy button (for STS-type connections)
// ---------------------------------------------------------------------------
function CopyPreConsentResourceButton({ agentId, resourceName }: { agentId: string; resourceName: string }) {
  const { toast } = useToast();

  const handleCopy = async () => {
    try {
      const cfg = await apiClient.getPublicConfig();
      const gatewayBaseUrl = cfg.gateway_base_url.replace(/\/$/, '');
      const url = `${gatewayBaseUrl}/oauth/pre-consent?agent_id=${encodeURIComponent(agentId)}&resource=${encodeURIComponent(resourceName)}`;
      await navigator.clipboard.writeText(url);
      toast({ title: 'Copied!', description: `Pre-consent link for ${resourceName} copied to clipboard.` });
    } catch {
      toast({ title: 'Error', description: 'Failed to copy to clipboard.', variant: 'destructive' });
    }
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-7 text-xs px-2"
      onClick={handleCopy}
      title={`Copy pre-consent link for ${resourceName}`}
    >
      <Copy className="w-3 h-3 mr-1" /> Pre-consent
    </Button>
  );
}

// ---------------------------------------------------------------------------
// Tools Tab
// ---------------------------------------------------------------------------
function ToolsTab({ detail, agentId }: { detail: AgentDetail | null; agentId: string }) {
  const { toast } = useToast();
  const [tools, setTools] = useState<AgentToolsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [includeDisabled, setIncludeDisabled] = useState(false);
  const [limit, setLimit] = useState(100);
  const [offset, setOffset] = useState(0);

  // Reset to page 1 if includeDisabled flips (changes the matched set).
  useEffect(() => { setOffset(0); }, [includeDisabled]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Pagination is over the FLAT tool list across all the agent's
      // accessible resources. The backend re-groups the page into the
      // {resources: [...]} shape — group `tool_count` is the per-page
      // count, not the per-resource total (full per-resource counts are
      // surfaced on the resources list page via tool-counts).
      const res = await apiClient.getAgentTools(agentId, {
        includeDisabled, limit, offset,
      });
      setTools(res);
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [agentId, includeDisabled, limit, offset, toast]);

  useEffect(() => { load(); }, [load]);

  const linkedResources = (detail?.connections || [])
    .map((c) => c.resource)
    .filter((r): r is NonNullable<typeof r> => !!r && r.enabled !== false && !!r.url);

  if (linkedResources.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <Wrench className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p>No tools available.</p>
        <p className="text-sm mt-1">Set a URL and enable a resource on one of this agent&apos;s connections.</p>
      </div>
    );
  }

  const byResource = new Map(
    (tools?.resources || []).map((g) => [g.resource_name, g]),
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Tools from {linkedResources.length} linked resource{linkedResources.length !== 1 ? 's' : ''}.
          Tool names are namespaced as <code className="text-xs">resource_name__tool_name</code> in the unified endpoint.
        </p>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs cursor-pointer">
            <input
              type="checkbox"
              checked={includeDisabled}
              onChange={(e) => setIncludeDisabled(e.target.checked)}
              className="h-3.5 w-3.5"
            />
            Show disabled tools
          </label>
          <Button variant="ghost" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>
      {linkedResources.map((resource) => {
        const group = byResource.get(resource.name);
        return (
          <Card key={resource.name}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Server className="w-4 h-4" />
                <Link
                  to={`/dashboard/resources/${encodeURIComponent(resource.name)}`}
                  className="hover:underline"
                >
                  {resource.name}
                </Link>
                <span className="text-xs bg-muted px-2 py-0.5 rounded font-normal">{resource.type || 'mcp'}</span>
                {group && (
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded font-normal">
                    {group.tool_count} tool{group.tool_count !== 1 ? 's' : ''}
                  </span>
                )}
              </CardTitle>
              <p className="text-xs text-muted-foreground">{resource.url}</p>
            </CardHeader>
            <CardContent>
              {group && group.tools.length > 0 ? (
                <ul className="space-y-2">
                  {group.tools.map((t) => {
                    const isDisabled = t.enabled === false;
                    return (
                      <li key={t.name} className="text-sm flex items-start gap-2">
                        <code className={`text-xs bg-muted px-1.5 py-0.5 rounded ${isDisabled ? 'line-through text-muted-foreground' : ''}`}>
                          {t.name}
                        </code>
                        <ToolSensitivityChip sensitivity={t.sensitivity ?? null} />
                        {isDisabled && (
                          <span
                            title={t.hidden_reason || 'Disabled'}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-200"
                          >
                            disabled
                          </span>
                        )}
                        {t.description && (
                          <span className="text-muted-foreground">
                            {t.description.length > 120 ? `${t.description.slice(0, 120)}…` : t.description}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No tools recorded yet. For active resources, run discovery on the
                  resource detail page; for STS resources, tools appear after the
                  first user call passes through the adapter.
                </p>
              )}
            </CardContent>
          </Card>
        );
      })}

      {tools && tools.pagination && (
        <Pagination
          limit={limit}
          offset={offset}
          total={tools.pagination.total}
          onLimitChange={setLimit}
          onOffsetChange={setOffset}
        />
      )}
    </div>
  );
}

function ToolSensitivityChip({ sensitivity }: { sensitivity: 'low' | 'medium' | 'high' | 'critical' | null }) {
  const styles: Record<string, string> = {
    unclassified: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
    low: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    high: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
    critical: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  };
  const key = sensitivity ?? 'unclassified';
  const label = sensitivity ?? 'unclassified';
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${styles[key]}`}>
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Policies Tab
// ---------------------------------------------------------------------------
function PoliciesTab({ agentId }: { agentId: string }) {
  const { toast } = useToast();
  const [policies, setPolicies] = useState<CedarPolicy[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    apiClient
      .listCedarPolicies()
      .then((all) => {
        // client-side filter: policies mentioning this agent id in their text
        const agentRef = `"${agentId}"`;
        setPolicies(all.filter((p) => p.policy_text.includes(agentRef)));
      })
      .catch((err: any) => {
        toast({ title: 'Error', description: err.message || 'Failed to load policies', variant: 'destructive' });
        setPolicies([]);
      })
      .finally(() => setLoading(false));
  }, [agentId, toast]);

  if (loading) {
    return <div className="text-center py-8 text-muted-foreground">Loading Cedar policies…</div>;
  }

  if (policies.length === 0) {
    return (
      <div className="text-center py-10 text-muted-foreground">
        <p className="font-medium">No Cedar policies reference this agent.</p>
        <p className="text-sm mt-1">
          Policies that include <code className="font-mono">&quot;{agentId}&quot;</code> in their text will appear here.
        </p>
        <Link to="/dashboard/policies" className="mt-3 inline-block text-sm text-primary hover:underline">
          Create a policy
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">{policies.length} Cedar {policies.length === 1 ? 'policy' : 'policies'} reference this agent.</p>
      <div className="border rounded-md overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="text-left p-3 text-sm font-medium text-muted-foreground">Enabled</th>
              <th className="text-left p-3 text-sm font-medium text-muted-foreground">Policy ID</th>
              <th className="text-left p-3 text-sm font-medium text-muted-foreground">Effect</th>
              <th className="text-left p-3 text-sm font-medium text-muted-foreground">Category</th>
            </tr>
          </thead>
          <tbody>
            {policies.map((p) => (
              <tr key={p.policy_id} className="border-b last:border-0 hover:bg-muted/30">
                <td className="p-3">
                  <span
                    className={`inline-block w-2 h-2 rounded-full ${p.enabled ? 'bg-green-500' : 'bg-gray-300'}`}
                    title={p.enabled ? 'Enabled' : 'Disabled'}
                  />
                </td>
                <td className="p-3 text-sm font-mono">
                  <Link
                    to={`/dashboard/policies/${encodeURIComponent(p.policy_id)}`}
                    className="text-primary hover:underline"
                  >
                    {p.policy_id}
                  </Link>
                </td>
                <td className="p-3">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium uppercase ${
                      p.effect === 'permit'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    }`}
                  >
                    {p.effect}
                  </span>
                </td>
                <td className="p-3">
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                    {p.category.replace(/_/g, ' ')}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Audit Tab
// ---------------------------------------------------------------------------
function AuditTab({ agentId }: { agentId: string }) {
  const { toast } = useToast();
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const log = await apiClient.getAgentAuditLog(agentId);
      setEntries(log);
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [agentId, toast]);

  useEffect(() => { load(); }, [load]);

  const formatTimestamp = (ts?: string) => {
    if (!ts) return '—';
    const d = new Date(ts);
    if (isNaN(d.getTime())) return ts;
    return d.toLocaleString();
  };

  const badgeColor = (action: string) => {
    if (action.includes('created')) return 'bg-green-100 text-green-800';
    if (action.includes('updated')) return 'bg-blue-100 text-blue-800';
    if (action.includes('deleted')) return 'bg-red-100 text-red-800';
    if (action.includes('failure') || action.includes('rejected') || action.includes('denied'))
      return 'bg-red-100 text-red-800';
    if (action.includes('success') || action.includes('validated') || action.includes('granted'))
      return 'bg-green-100 text-green-800';
    return 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {entries.length} audit entr{entries.length === 1 ? 'y' : 'ies'}
        </p>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Loading audit log...</div>
      ) : entries.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">No audit entries found.</div>
      ) : (
        <div className="space-y-3">
          {entries.map((entry, index) => (
            <div key={entry.id || index} className="border rounded-lg p-4 hover:bg-muted/30 transition">
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${badgeColor(entry.action)}`}>
                    {entry.action}
                  </span>
                  {entry.entity_type && (
                    <span className="text-sm text-muted-foreground">
                      {entry.entity_type}
                      {entry.entity_id && entry.entity_id !== agentId && `: ${entry.entity_id}`}
                    </span>
                  )}
                </div>
                <span className="text-sm text-muted-foreground">{formatTimestamp(entry.changed_at)}</span>
              </div>
              {entry.changed_by && (
                <div className="text-sm text-muted-foreground mb-2">
                  User: <span className="font-mono">{entry.changed_by}</span>
                </div>
              )}
              {entry.changes && Object.keys(entry.changes).length > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-sm text-blue-600 hover:text-blue-800">View Changes</summary>
                  <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-x-auto">
                    {JSON.stringify(entry.changes, null, 2)}
                  </pre>
                </details>
              )}
              {entry.details && Object.keys(entry.details).length > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-sm text-blue-600 hover:text-blue-800">View Details</summary>
                  <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-x-auto">
                    {JSON.stringify(entry.details, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Client Secret Proxy Section
// Compatibility shim for clients (e.g. Gemini) that require a static
// client_id + client_secret in their config and do not support DCR or CIMD.
// The gateway verifies the proxy secret at /oauth2/v1/token and substitutes
// the real Okta client_secret on the upstream call.
// ---------------------------------------------------------------------------
function ClientSecretProxySection({ agentId }: { agentId: string }) {
  const { toast } = useToast();
  const [status, setStatus] = useState<ProxySecretStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [issuing, setIssuing] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [revealing, setRevealing] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null);
  const [showSecret, setShowSecret] = useState(false);
  const [copied, setCopied] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const s = await apiClient.getProxySecretStatus(agentId);
      setStatus(s);
    } catch (err: any) {
      if (err.status !== 401) {
        toast({
          title: 'Error',
          description: err.message || 'Failed to load proxy secret status.',
          variant: 'destructive',
        });
      }
    } finally {
      setLoading(false);
    }
  }, [agentId, toast]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // Auto-clear revealed plaintext after 5 minutes — minimizes screen-scrape exposure.
  // Also reset to masked whenever a new secret arrives (mint/rotate/reveal).
  useEffect(() => {
    if (!revealedSecret) return;
    setShowSecret(false);
    const t = setTimeout(() => setRevealedSecret(null), 5 * 60 * 1000);
    return () => clearTimeout(t);
  }, [revealedSecret]);

  const handleIssue = async () => {
    setIssuing(true);
    try {
      const result = await apiClient.issueProxySecret(agentId);
      setRevealedSecret(result.secret);
      setStatus(result);
      toast({ title: 'Issued', description: 'Proxy secret issued. Copy the value now.' });
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to issue proxy secret.',
        variant: 'destructive',
      });
    } finally {
      setIssuing(false);
    }
  };

  const handleRotate = async () => {
    if (!confirm('Rotate proxy secret? The current value will be invalidated immediately.')) return;
    setRotating(true);
    try {
      const result = await apiClient.rotateProxySecret(agentId);
      setRevealedSecret(result.secret);
      setStatus(result);
      toast({ title: 'Rotated', description: 'Proxy secret rotated. Copy the new value now.' });
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to rotate proxy secret.',
        variant: 'destructive',
      });
    } finally {
      setRotating(false);
    }
  };

  const handleReveal = async () => {
    setRevealing(true);
    try {
      const result = await apiClient.revealProxySecret(agentId);
      setRevealedSecret(result.secret);
      setStatus(result);
      toast({ title: 'Revealed', description: 'Plaintext displayed. This event was logged.' });
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to reveal proxy secret.',
        variant: 'destructive',
      });
    } finally {
      setRevealing(false);
    }
  };

  const handleRevoke = async () => {
    setRevoking(true);
    try {
      await apiClient.revokeProxySecret(agentId);
      setRevealedSecret(null);
      toast({ title: 'Revoked', description: 'Proxy secret revoked.' });
      await loadStatus();
      setRevokeOpen(false);
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to revoke proxy secret.',
        variant: 'destructive',
      });
    } finally {
      setRevoking(false);
    }
  };

  const handleCopy = async () => {
    if (!revealedSecret) return;
    try {
      await navigator.clipboard.writeText(revealedSecret);
      setCopied(true);
      toast({ title: 'Copied!', description: 'Proxy secret copied to clipboard.' });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to copy to clipboard.',
        variant: 'destructive',
      });
    }
  };

  const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '—');
  const enabled = status?.enabled ?? false;
  const anyBusy = issuing || rotating || revealing || revoking;

  return (
    <div className="space-y-3 pt-2">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading proxy secret status...
          </div>
        ) : (
          <>
            <div className="space-y-1.5 text-sm">
              <div className="flex items-center gap-2">
                {enabled ? (
                  <Check className="w-4 h-4 text-green-600" />
                ) : (
                  <X className="w-4 h-4 text-muted-foreground" />
                )}
                <span>{enabled ? 'Configured' : 'Not configured'}</span>
                {enabled && status?.prefix && (
                  <span className="text-xs text-muted-foreground font-mono">
                    {status.prefix}…
                  </span>
                )}
              </div>
              {enabled && (
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground pl-6">
                  <span>Issued: {fmt(status?.created_at ?? null)}</span>
                  <span>Last rotated: {fmt(status?.rotated_at ?? null)}</span>
                  <span>Last revealed: {fmt(status?.revealed_at ?? null)}</span>
                  <span>Last used: {fmt(status?.last_used_at ?? null)}</span>
                </div>
              )}
            </div>

            {revealedSecret && (
              <div className="space-y-2 rounded border border-amber-300 bg-amber-50 p-3">
                <div className="flex items-center gap-2">
                  <Input
                    readOnly
                    type={showSecret ? 'text' : 'password'}
                    value={revealedSecret}
                    className="font-mono text-xs flex-1"
                    onClick={(e) => (e.target as HTMLInputElement).select()}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowSecret((v) => !v)}
                    aria-label={showSecret ? 'Hide secret' : 'Show secret'}
                    title={showSecret ? 'Hide' : 'Show'}
                  >
                    {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleCopy}>
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    <span className="ml-1.5">{copied ? 'Copied' : 'Copy'}</span>
                  </Button>
                </div>
                <p className="text-xs text-amber-900">
                  Hidden by default — click the eye icon to view, or Copy to grab the
                  value without revealing it. The value will clear from this view in 5
                  minutes. An admin can re-display it via Reveal — every reveal is
                  audit-logged.
                </p>
              </div>
            )}

            <div className="flex flex-wrap gap-2 pt-1">
              {!enabled ? (
                <Button onClick={handleIssue} disabled={anyBusy}>
                  {issuing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Key className="w-4 h-4 mr-2" />
                  )}
                  Issue proxy secret
                </Button>
              ) : (
                <>
                  <Button variant="outline" onClick={handleRotate} disabled={anyBusy}>
                    {rotating ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    Rotate
                  </Button>
                  <Button variant="outline" onClick={handleReveal} disabled={anyBusy}>
                    {revealing ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Key className="w-4 h-4 mr-2" />
                    )}
                    Reveal
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setRevokeOpen(true)}
                    disabled={anyBusy}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Revoke
                  </Button>
                </>
              )}
            </div>
          </>
        )}

      <Dialog open={revokeOpen} onOpenChange={setRevokeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke proxy secret?</DialogTitle>
            <DialogDescription>
              The current proxy secret will be invalidated immediately. Any client using
              it will start receiving 401 errors at the gateway&apos;s /oauth2/v1/token
              endpoint. To use the proxy again, an admin will need to issue a new secret
              and update the client config. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRevokeOpen(false)}
              disabled={revoking}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRevoke}
              disabled={revoking}
            >
              {revoking ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Revoke
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
